<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
defineEmits(["input"]);
import { Vue } from 'vue-property-decorator';
import { onMounted } from 'vue';
import i18n from '@/i18n';
import {
  CustomMetadataService,
  type EntityCustomMetadataDto,
  type EntityCustomMetadataEntryLabelDto,
  type EntityCustomMetadataEntryUserDto,
  type GetCustomMetadataListResponseDto,
  type EntityCustomMetadataEntryTeamDto,
  type EntityCustomMetadataEntryStringDto,
} from '@/api';
import { AssetsTypesEnum } from '@/components/custom-metadata/CustomMetadata';
import SLabel from '@/components/SLabel.vue';
import CustomMetadataLabelSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataLabelSelector.vue';
import CustomMetadataStringSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataStringSelector.vue';
import CustomMetadataUserSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataUserSelector.vue';
import CustomMetadataTeamSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataTeamSelector.vue';
/**
 *
 * Custom Metadata Selector
 * Used to attach custom metadata entries to any entity
 *
 */

interface CustomMetadataSelectorProps {
  assetType: AssetsTypesEnum;
  entityCustomMetadata: EntityCustomMetadataDto[];
  labelsAsColumns?: boolean;
}

const props = defineProps({
  assetType: null,
  entityCustomMetadata: null,
  labelsAsColumns: { type: Boolean, default: false },
  value: null
});

const { value: customMetadataModel } = __MACROS_useVModel(["value", undefined, "input"]);

let fetchedMetadata = _ref<GetCustomMetadataListResponseDto[]>([]);

// Filter metadata based on the asset type
const filteredMetadata = _computed(() => fetchedMetadata.value.filter((metadata) => metadata.authorizedAssets.includes(props.assetType)));

// Constants for metadata types
const METADATA_TYPES = {
  LABEL: 'LABEL',
  STRING: 'STRING',
  USER: 'USER',
  TEAM: 'TEAM',
};

/**
 * Get the existing entity custom metadata from the metadata ID
 * @param metadataId
 */
const getEntityCustomMetadataFromMetadataId = (metadataId: string) => props.entityCustomMetadata.find((metadata) => metadata.id === metadataId);

/**
 * Handles the change event from the sub-selectors:
 * Create a deep copy of entityCustomMetadata and update model
 */
const onSubSelectorUpdate = (
  metadataId: string,
  entries: EntityCustomMetadataEntryLabelDto[] | EntityCustomMetadataEntryUserDto[] | EntityCustomMetadataEntryTeamDto[],
) => {
  const updatedCustomMetadata: EntityCustomMetadataDto[] = filteredMetadata.value.map((metadata) => {
    const existing = props.entityCustomMetadata.find((m) => m.id === metadata.id);
    return {
      id: metadata.id,
      entries: metadata.id === metadataId
        ? (entries as (EntityCustomMetadataEntryLabelDto | EntityCustomMetadataEntryUserDto | EntityCustomMetadataEntryTeamDto)[])
        : (existing?.entries ?? []),
    } as EntityCustomMetadataDto;
  });
  customMetadataModel.value = updatedCustomMetadata;
};

// Type-safe wrapper functions for each selector type
const onStringSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryStringDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};
const onLabelSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryLabelDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};

const onUserSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryUserDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};

const onTeamSelectorUpdate = (metadataId: string, entries: EntityCustomMetadataEntryTeamDto[]) => {
  onSubSelectorUpdate(metadataId, entries);
};

onMounted(() => {
  // Fetch custom metadata when component is mounted
  CustomMetadataService.getAllCustomMetadata()
    .then((response) => {
      fetchedMetadata.value = response.data || [];
    })
    .catch(() => {
      Vue.notify({
        title: i18n.t('custom_metadata.fetch.error_title'),
        text: i18n.t('custom_metadata.fetch.error_text'),
        type: 'error',
      });
    });
});
</script>

<template lang="pug">
.custom-metadata-selector
  .custom-metadata-form-item(
    v-for="metadata in filteredMetadata"
    :key="metadata.id"
    class="my-4"
  )

    SLabel( :asColumns="props.labelsAsColumns" )
      span {{ metadata.name }}
      v-tooltip( top max-width="300" v-if="metadata.description")
        template(v-slot:activator='{ on }')
          span(v-on="on")
            SIcon.ml-1.mt-n1( icon="icon-question-circle-outline" v-on="on" size="small" color="iconNeutral")
        span {{ metadata.description }}
        div.pt-4.tooltip-arrow-bottom
      CustomMetadataLabelSelector(
        v-if="metadata.type === METADATA_TYPES.LABEL"
        slot="input"
        :metadata="metadata"
        :entity-custom-metadata="getEntityCustomMetadataFromMetadataId(metadata.id)"
        @update="onLabelSelectorUpdate"
      )

      CustomMetadataStringSelector(
        v-else-if="metadata.type === METADATA_TYPES.STRING"
        slot="input"
        :metadata="metadata"
        :entity-custom-metadata="getEntityCustomMetadataFromMetadataId(metadata.id)"
        @update="onStringSelectorUpdate"
      )

      CustomMetadataUserSelector(
        v-else-if="metadata.type === METADATA_TYPES.USER"
        slot="input"
        :metadata="metadata"
        :entity-custom-metadata="getEntityCustomMetadataFromMetadataId(metadata.id)"
        @update="onUserSelectorUpdate"
      )

      CustomMetadataTeamSelector(
        v-else-if="metadata.type === METADATA_TYPES.TEAM"
        slot="input"
        :metadata="metadata"
        :entity-custom-metadata="getEntityCustomMetadataFromMetadataId(metadata.id)"
        @update="onTeamSelectorUpdate"
      )

</template>
